/**
 * useUnifiedBetCard.js — data layer for UnifiedBetCard.
 * Extracted from components/UnifiedBetCard.jsx (god-file decomposition).
 * Owns all state, effects, memo, handlers, and derived values for the card
 * and returns them for UnifiedBetCardView to render.
 */

import React, { useState, useMemo, useRef, useEffect, useCallback, useContext } from 'react';
import TickerContext from '../context/tickerContextObject';
import { marketAverageAmerican, resolveCardKickoff } from '../utils/cardDisplay';
import { sideOfLine, cardSides as listCardSides, defaultCardSide } from '../utils/cardSides';
import { Platform, Animated, Easing, useWindowDimensions } from 'react-native';
import { computeDevigMethodsSnapshot, getTopLevelConsensusFairProbs, cleanPerBookEv, resolveConsensusFair, sgpDevigMethodsSnapshot, straightDevigMethodsSnapshot, isUnbackableBookData, isLayBookName } from '../utils/canonicalEv';
import * as betMath from '../utils/betMath';
import { isNoEvRow } from '../utils/searchDisplay';
import { dedupeCardBooks, cardBookCount, lineBookOdds } from '../utils/cardBooks';
import { useConsensusOptions } from '../utils/useConsensusOptions';
import { scoreMyBooksOverlap, normBookName } from '../../utils/resultSorting';
import {
    formatOdds, extractPrice, getImpliedProbability, detectSportType,
    sanitizeSelectionString, isPlayerPropMarket, isTeamPropWithAlts,
} from '../utils/betCardFormatters';

/**
 * Numeric line for a card line. Some sources send "over"/"under" as the line
 * value; fall back to the number in the selection text ("Over 27.5",
 * "Hurricanes -1.5"). null when the market has no line.
 */
export function lineNumberOf(line) {
    for (const candidate of [line?.lineValue, line?.raw?.selection_line, line?.raw?.handicap, line?.raw?.line, line?.raw?.points, line?.selection_line]) {
        if (candidate === null || candidate === undefined) continue;
        if (typeof candidate === 'number') { if (Number.isFinite(candidate)) return candidate; continue; }
        const trimmed = String(candidate).trim();
        if (trimmed === '') continue;
        const n = Number(trimmed);
        if (Number.isFinite(n)) return n;
    }
    const text = String(line?.raw?.selection || line?.selection || line?.title || '');
    // Trailing number must stand alone ("Hurricanes -1.5"), not end a score ("2-1").
    const m = text.match(/\b(?:over|under)\s*([+-]?\d+(?:\.\d+)?)\b/i) || text.match(/(?:^|\s)([+-]\d+(?:\.\d+)?|\d+\.\d+)\s*$/);
    return m ? Number(m[1]) : null;
}

export function useUnifiedBetCard({
    option,
    isSelected = false,
    onSelect,
    onOpenDeepLink,
    onWatchFixture,
    onUnwatchFixture,
    isWatching = false,
    // SGP mode
    isSGP = false,
    sgpLegs = [],
    // false when MorphReveal is revealing this card; its wipe is the entrance,
    // so the card starts settled instead of sliding under the placeholder.
    animateMount = true,
    // Stagger for cards that mount without the MorphReveal wipe (later pages,
    // filter changes): each card enters a beat after the previous one.
    mountDelay = 0,
    // Grouped lines mode (for player/team props)
    groupedLines: allGroupedLines = null, // Array of lines for this player/market (both sides)
    // ⭐ NEW: Requested line for centering/highlighting (e.g., +14 from "pistons +14 turnovers")
    requestedLine = null, // The exact line value user asked for (e.g., 14)
    requestedDirection = null, // 'over' or 'under' from query
    referenceTime = null, // freeze/archive clock: game status (UPCOMING/FINAL) reads it, like the ticker
    // True when the user picked an explicit Additional Sorting option (odds, date,
    // books, market): groupedLines arrive in that order and the card keeps it
    // until the user clicks one of the card's own line-sort buttons.
    preserveLineOrder = false,
    // Math props
    canShowMath = true,
    showAllMath = true,
    isAdmin = false,
    nerdMathDefaultOn = false,
    showAltsExpanded = true, // ⭐ NEW: Default to showing alt lines expanded
    unitSize = 10,
    kellyCriterion = 0.25,
    marketPeers = [],
    // Sportsbook props
    userSelectedSportsbooks = [],
    activeBookFilter = [],  // Active sportsbook chips from the results filter bar
    defaultSelectedBook = null,
    highlightPreferredBook = false,
    myBooks = [], // User's preferred "My Books" for sorting
    defaultBookSort = 'ev', // Default sort for the books panel: 'ev' | 'odds' | 'mybooks'
    // Initial line list filter: min distinct books (from profile minBooksStraights)
    initialMinBooksFilter,
    // Logos
    homeLogoUrl,
    awayLogoUrl,
    teamLogoUrl,
}) {
    const { width: windowWidth } = useWindowDimensions();
    const isMobileLayout = windowWidth < 768;
    const isCompactLayout = windowWidth < 480;

    // v1.15.x P3.3/P3.5 — user's consensus-exclusion preference. Passed into
    // every cleanPerBookEv / resolveConsensusFair call so the EV shown on the
    // card matches the policy the user picked, and used for per-book excluded
    // styling + the header pill indicator below.
    const consensusOptions = useConsensusOptions();
    const excludedBookSet = consensusOptions.excluded;
    const consensusPolicy = consensusOptions.policy;

    // Mount animation + card hover
    const mountAnim = useRef(new Animated.Value(animateMount ? 0 : 1)).current;

    useEffect(() => {
        if (!animateMount) return;
        // OS "reduce motion": the card is simply there (motion.js is not imported here; the
        // hook tests mock react-native too thinly for it).
        if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { mountAnim.setValue(1); return; }
        Animated.timing(mountAnim, {
            toValue: 1,
            duration: 460,
            delay: mountDelay,
            easing: Easing.out(Easing.exp),
            useNativeDriver: true,
        }).start();
    }, []);

    // State
    const [showMath, setShowMath] = useState(showAllMath);
    const [showNerdMath, setShowNerdMath] = useState(nerdMathDefaultOn);
    const [showAllBooks, setShowAllBooks] = useState(true); // Show by default
    const [showAltLines, setShowAltLines] = useState(showAltsExpanded); // ⭐ Use user preference
    const [selectedBook, setSelectedBook] = useState(defaultSelectedBook || 'best');
    const [sortBooksBy, setSortBooksBy] = useState(defaultBookSort); // 'ev', 'odds', 'name', 'mybooks'
    // Tracks whether the user manually picked a sort for the books panel.
    // We need this because `sortBooksBy` is initialized from `defaultBookSort` ONCE
    // (via useState), so if preferences hydrate async and the prop later flips from
    // 'ev' → 'mybooks', the state wouldn't otherwise update — and auto-snap would
    // keep picking the global top-EV book (e.g. Prophet X / Novig / offshore outliers)
    // instead of the user's top My Book. This ref lets a sync effect below safely
    // follow prop changes unless the user has explicitly overridden.
    const userHasSelectedSortRef = useRef(false);
    const [stake, setStake] = useState(unitSize);
    const [selectedLineIndex, setSelectedLineIndex] = useState(0);
    // Tracks whether the user has manually clicked a line — prevents the auto-select
    // effect from overriding their choice on subsequent re-renders.
    const userHasSelectedRef = useRef(false);

    // One card holds both sides (Over/Under, −/+) of a team or player market; the
    // switch shows one side's lines. Everything below reads `groupedLines`, the
    // active side's lines, so line picking, requested-line jumps and the
    // best-value pick work per side unchanged.
    const cardSides = useMemo(() => listCardSides(allGroupedLines), [allGroupedLines]);
    const [pickedSide, setPickedSide] = useState(null);
    // A new search (new named side or line) opens on its own side again.
    useEffect(() => { setPickedSide(null); }, [requestedDirection, requestedLine]);
    const activeSide = cardSides.length > 1
        ? (pickedSide && cardSides.includes(pickedSide) ? pickedSide : defaultCardSide(allGroupedLines, requestedDirection))
        : null;
    const groupedLines = useMemo(() => {
        if (!activeSide || !Array.isArray(allGroupedLines)) return allGroupedLines;
        return allGroupedLines.filter((l) => sideOfLine(l) === activeSide);
    }, [allGroupedLines, activeSide]);
    const setActiveSide = useCallback((side) => {
        if (!cardSides.includes(side)) return;
        // A new side: let the auto-select pick that side's line again.
        userHasSelectedRef.current = false;
        setPickedSide(side);
    }, [cardSides]);
    // ⭐ NEW: Line filtering toggles
    const [lineSortMode, setLineSortMode] = useState('centered'); // 'centered' (around requested), 'best_value', 'most_books'
    const [showMainOnly, setShowMainOnly] = useState(false); // Show only main lines
    const [minBooksFilter, setMinBooksFilter] = useState(() => {
        const v = parseInt(initialMinBooksFilter, 10);
        return Number.isFinite(v) && v > 0 ? v : 0;
    }); // Minimum sportsbooks required

    useEffect(() => {
        const v = parseInt(initialMinBooksFilter, 10);
        // 0 = "All" (the default while site_config/search_display.hideNoEvRows is off).
        if (Number.isFinite(v) && v >= 0) setMinBooksFilter(v);
    }, [initialMinBooksFilter]);
    
    // Ref for auto-scrolling to selected line
    const altLinesScrollRef = useRef(null);
    const LINE_ROW_HEIGHT = 52; // Approximate height of each line row
    
    // Sync showMath with prop
    useEffect(() => {
        setShowMath(showAllMath);
    }, [showAllMath]);
    
    // ⭐⭐⭐ NOTE: workingOption must use SORTED lines, not raw groupedLines!
    // This is computed AFTER sortedLines, so it must be defined after sortedLines memo
    
    // ⭐ Enhanced sorting: Center around requested line when available
    // Sort modes:
    // - 'centered': Show requested line first, then alternate above/below (13.5, 14.5, 12.5, 15.5, 11.5...)
    // - 'best_value': Sort by EV/value (best odds first)
    // - 'most_books': Sort by number of sportsbooks offering the line
    const sortedLines = useMemo(() => {
        if (!groupedLines || groupedLines.length === 0) return [];
        
        // ⭐ DEBUG: Log requestedLine value
        if (requestedLine !== null || requestedDirection !== null) {
            console.log('[UnifiedBetCard] 🎯 Sorting with requestedLine:', requestedLine, 'direction:', requestedDirection);
        }
        
        // Detect market type from first selection
        const firstSelection = (groupedLines[0]?.raw?.selection || groupedLines[0]?.selection || '').toLowerCase();
        const firstMarket = (groupedLines[0]?.raw?.market || groupedLines[0]?.title || '').toLowerCase();
        const isUnderMarket = /\bunder\b/i.test(firstSelection);
        const isSpreadMarket = firstMarket.includes('spread') || firstMarket.includes('handicap') || 
                               firstMarket.includes('run line') || firstMarket.includes('puck line');
        
        // Helper to extract line value
        const getLineValue = (line) => lineNumberOf(line) ?? 0;
        
        // Book count for a line: the deduped list the row and panel show (utils/cardBooks).
        const getBookCount = cardBookCount;
        
        // Filter by showMainOnly if enabled - uses the line with most sportsbooks as the "true main"
        let filteredLines = groupedLines;
        if (showMainOnly && groupedLines.length > 1) {
            // Find the line with the most sportsbooks
            let maxBooks = 0;
            let mainLine = groupedLines[0];
            groupedLines.forEach(l => {
                const bookCount = getBookCount(l);
                if (bookCount > maxBooks) {
                    maxBooks = bookCount;
                    mainLine = l;
                }
            });
            filteredLines = [mainLine];
        }
        
        // Filter by minBooksFilter
        if (minBooksFilter > 0) {
            filteredLines = filteredLines.filter(l => getBookCount(l) >= minBooksFilter);
        }
        
        // Additional Sorting chosen in the results bar: show lines in that order.
        if (preserveLineOrder && lineSortMode === 'centered') {
            return filteredLines;
        }

        // ⭐ CENTERED SORT MODE: When user requested a specific line, center around it
        // When no requestedLine, preserve original backend order (true "default")
        if (lineSortMode === 'centered') {
                    if (requestedLine !== null) {
                        // Find the exact match or closest line to the requested value
                        const targetLine = parseFloat(requestedLine);
                        const direction = requestedDirection || 'over'; // Default to over if not specified
                        
                        return [...filteredLines].sort((a, b) => {
                            const aLine = getLineValue(a);
                            const bLine = getLineValue(b);
                            
                            // ⭐ DIRECTION-AWARE SORTING:
                            // For "Over 24.5": prefer lines >= 24.5, closest first (25.5 before 26.5)
                            // For "Under 24.5": prefer lines <= 24.5, closest first (23.5 before 22.5)
                            
                            const aDist = Math.abs(aLine - targetLine);
                            const bDist = Math.abs(bLine - targetLine);
                            
                            // Exact match always first
                            if (aDist === 0 && bDist !== 0) return -1;
                            if (bDist === 0 && aDist !== 0) return 1;
                            
                            // ⭐ Direction-aware: prefer lines in the "correct" direction
                            if (direction === 'over') {
                                // For Over bets: prefer lines >= target (25.5 over 23.5 when target is 24.5)
                                const aInDirection = aLine >= targetLine;
                                const bInDirection = bLine >= targetLine;
                                if (aInDirection && !bInDirection) return -1;
                                if (!aInDirection && bInDirection) return 1;
                            } else if (direction === 'under') {
                                // For Under bets: prefer lines <= target (23.5 over 25.5 when target is 24.5)
                                const aInDirection = aLine <= targetLine;
                                const bInDirection = bLine <= targetLine;
                                if (aInDirection && !bInDirection) return -1;
                                if (!aInDirection && bInDirection) return 1;
                            }
                            
                            // Within same direction, sort by distance (closest first)
                            if (aDist !== bDist) return aDist - bDist;
                            
                            // For same distance, prefer the one with more books
                            return getBookCount(b) - getBookCount(a);
                        });
                    } else {
                        // ⭐ DEFAULT SORT: Pure NUMERIC value ascending (ignore main/alt status)
                        // For spreads: -5, -5.5, -6, -6.5 etc.
                        // For totals: 200.5, 201, 201.5 etc.
                        return [...filteredLines].sort((a, b) => {
                            const aLine = getLineValue(a);
                            const bLine = getLineValue(b);
                            return aLine - bLine; // Ascending numeric order
                        });
                    }
        }
        
        // ⭐ MOST BOOKS SORT: Prioritize lines with most sportsbook coverage
        if (lineSortMode === 'most_books') {
            return [...filteredLines].sort((a, b) => {
                const aBooks = getBookCount(a);
                const bBooks = getBookCount(b);
                if (aBooks !== bBooks) return bBooks - aBooks;
                
                // Secondary sort: by numeric line value
                const aLine = getLineValue(a);
                const bLine = getLineValue(b);
                return aLine - bLine;
            });
        }
        
        // ⭐ EV% SORT (best_value): Sort by EV% highest to lowest (ignore main/alt status)
        // Helper to get EV% for a line
        const getLineEv = (line) => {
            // Try to get EV from the line's best book
            const bookOdds = lineBookOdds(line);
            const bestBookEv = Object.entries(bookOdds).reduce((best, [book, curr]) => {
                if (isUnbackableBookData(curr, book)) return best;
                const ev = curr?.ev_percent ?? null;
                if (ev === null) return best;
                if (best === null) return ev;
                return ev > best ? ev : best;
            }, null);
            // Fall back to line-level EV if available
            return bestBookEv ?? line?.ev_percent ?? line?.raw?.ev_percent ?? -999;
        };
        
        return [...filteredLines].sort((a, b) => {
            // Sort purely by EV% descending (highest EV first)
            const aEv = getLineEv(a);
            const bEv = getLineEv(b);
            return bEv - aEv; // Descending order
        });
    }, [groupedLines, requestedLine, requestedDirection, lineSortMode, showMainOnly, minBooksFilter, preserveLineOrder]);
    
    // ⭐ Auto-select line when sorting changes (must be after sortedLines definition)
    // A line the user clicked stays selected when the lines are re-sorted (card sort
    // buttons or Additional Sorting): selectedLineIndex is positional, so re-find it.
    const pickedLineRef = useRef(null);
    const prevSortedLinesRef = useRef(sortedLines);
    useEffect(() => {
        const sortChanged = prevSortedLinesRef.current !== sortedLines;
        prevSortedLinesRef.current = sortedLines;
        if (!userHasSelectedRef.current) { pickedLineRef.current = null; return; }
        if (sortChanged && pickedLineRef.current) {
            const idx = sortedLines.indexOf(pickedLineRef.current);
            if (idx >= 0) {
                if (idx !== selectedLineIndex) setSelectedLineIndex(idx);
                return;
            }
        }
        pickedLineRef.current = sortedLines[selectedLineIndex] || null;
    }, [sortedLines, selectedLineIndex]);

    // Reset the user-selection guard whenever the query itself changes so the new
    // requestedLine / requestedDirection gets auto-applied on the fresh results.
    useEffect(() => {
        userHasSelectedRef.current = false;
    }, [requestedLine, requestedDirection]);

    // Auto-select the best line on initial render and when the query changes.
    // Does NOT run if the user has already manually clicked a line (userHasSelectedRef).
    // - For specific queries: Uses DIRECTION-AWARE matching (Over 24.5 → prefer 25.5 over 23.5)
    // - For generic queries (from ticker): Select the MAIN line (most sportsbooks) by default
    useEffect(() => {
        if (sortedLines.length === 0) return;
        // Don't override an explicit user click — only auto-select on fresh data.
        if (userHasSelectedRef.current) return;
        
        if (requestedLine !== null) {
            // Specific line requested - find closest match with direction awareness
            const targetLine = parseFloat(requestedLine);
            const direction = requestedDirection || 'over';
            
            let bestIdx = 0;
            let bestDist = Infinity;
            let bestInDirection = false;
            
            sortedLines.forEach((line, idx) => {
                const lineVal = (lineNumberOf(line) ?? 0);
                // Use absolute-value distance so spread lines like -4.5 match a requestedLine of 4.5.
                // Without this, Math.abs(-4.5 - 4.5)=9 loses to Math.abs(-0.5 - 4.5)=5 incorrectly.
                const dist = Math.abs(Math.abs(lineVal) - Math.abs(targetLine));
                
                // Check if this line is in the "correct" direction
                const inDirection = direction === 'over' 
                    ? lineVal >= targetLine 
                    : lineVal <= targetLine;
                
                // Prefer lines in the correct direction
                if (inDirection && !bestInDirection) {
                    bestIdx = idx;
                    bestDist = dist;
                    bestInDirection = true;
                } else if (inDirection === bestInDirection && dist < bestDist) {
                    bestIdx = idx;
                    bestDist = dist;
                } else if (!bestInDirection && !inDirection && dist < bestDist) {
                    bestIdx = idx;
                    bestDist = dist;
                }
            });
            
            setSelectedLineIndex(bestIdx);
        } else if (preserveLineOrder) {
            // Explicit Additional Sorting: the top line of that ordering is the pick.
            setSelectedLineIndex(0);
        } else {
            // v1.16.6 — Generic query: DEFAULT TO HIGHEST-EV LINE.
            // Previous v1.16.4 behavior defaulted to the main line, but that was
            // user-reported wrong: when the user types "Player Points Over"
            // (with no line), they want the best edge surfaced — not the book's
            // designated primary. Main-line preselection is only correct when
            // the user literally typed a specific line (`requestedLine != null`
            // branch above handles that path). Aligns pill, auto-select, and
            // list sort on "best EV" for all generic queries.
            const _mbActive = sortBooksBy === 'mybooks' && Array.isArray(myBooks) && myBooks.length > 0;
            const _mbNorm = _mbActive ? new Set(myBooks.map(b => normBookName(b))) : null;
            const bestIdx = _pickBestEvLineIdx(sortedLines, { myBooksNorm: _mbNorm, minBooks: 5 });
            setSelectedLineIndex(bestIdx);
        }
    // sortBooksBy / myBooks intentionally omitted: the picker is fine to recompute on
    // the next sortedLines change. Including them would stomp the user's manual pick
    // every time they flip the books panel sort.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedLine, requestedDirection, sortedLines, preserveLineOrder]);
    
    // ⭐ Auto-scroll to center the selected line in the ScrollView
    // The selected line is shown both in the main section AND in the scroll area (highlighted)
    useEffect(() => {
        if (altLinesScrollRef.current && showAltLines && sortedLines.length > 1) {
            // Calculate scroll position to center the selected line
            const scrollToIndex = selectedLineIndex;
            const scrollPosition = Math.max(0, (scrollToIndex * LINE_ROW_HEIGHT) - (160 / 2) + (LINE_ROW_HEIGHT / 2));
            
            // Small delay to ensure ScrollView is rendered
            setTimeout(() => {
                altLinesScrollRef.current?.scrollTo?.({ y: scrollPosition, animated: true });
            }, 100);
        }
    }, [selectedLineIndex, showAltLines, sortedLines.length]);
    
    // ⭐ Determine the TRUE main line: always the line offered by the most sportsbooks.
    // This is independent of requestedLine — "MAIN" is a market property, not a query property.
    // The REQUESTED badge separately marks whichever line is closest to what the user asked for.
    const { trueMainLine, trueMainLineIndex } = useMemo(() => {
        if (!sortedLines || sortedLines.length === 0) return { trueMainLine: null, trueMainLineIndex: -1 };
        
        let bestLine = sortedLines[0];
        let bestIdx = 0;
        let maxBooks = 0;
        
        sortedLines.forEach((line, idx) => {
            const bookCount = cardBookCount(line);
            if (bookCount > maxBooks) {
                maxBooks = bookCount;
                bestLine = line;
                bestIdx = idx;
            }
        });
        
        return { trueMainLine: bestLine, trueMainLineIndex: bestIdx };
    }, [sortedLines]);
    
    // Helper to check if a line is the true main (based on book count, not backend flag)
    const isTrueMainLine = (line) => line === trueMainLine;
    
    const altLines = sortedLines.filter(l => l !== trueMainLine);
    const hasAltLines = altLines.length > 0;
    
    // ⭐ Track if ORIGINAL data has alt lines (before filtering) - for showing controls even when filtered empty
    const hasOriginalAltLines = groupedLines && groupedLines.length > 1;
    
    // ⭐⭐⭐ Get working option (for grouped lines, use SORTED line at selected index)
    // CRITICAL: Use sortedLines[selectedLineIndex], NOT groupedLines[selectedLineIndex]!
    // When the user hasn't manually clicked a line, compute the optimal index synchronously
    // so the FIRST render shows the correct line (the highest-EV achievable line, or the
    // line closest to requested).
    //
    // ⭐ PILL–SORT ALIGNMENT (v1.12.x): for generic queries (no requestedLine), pick the
    // HIGHEST-EV line — with a minimum-books guard so we never surface a 2-book outlier.
    // Previous behavior picked "line with most books" (consensus/main line), which made
    // the pill display the safest/smoothest EV while the results-list sort used the best
    // alt line's EV. User saw cards appear scrambled because sort rank (best alt) didn't
    // match the pill (main line). Aligning workingOption to the highest-EV line fixes it
    // and also surfaces the best achievable EV on the pill, which is the product promise.
    // v1.16.6 — Best-EV line picker for generic queries.
    // Each line gets a score = max cleanPerBookEv across the relevant book-set
    // (user's myBooks when `myBooksNorm` is provided, otherwise all books).
    // The `minBooks` guard prevents a 2-book outlier line from winning over a
    // well-sampled main line. If no line clears the guard we fall back to the
    // most-books line (matches legacy behavior).
    //
    // History: v1.16.4 briefly defaulted to the main line here, but that was
    // wrong for generic queries — users want the best edge surfaced, not the
    // book's designated primary. Specific-line queries use the
    // `requestedLine != null` branch above, which honors the exact line.
    const _pickBestEvLineIdx = (lines, { myBooksNorm = null, minBooks = 5 } = {}) => {
        if (!lines || lines.length === 0) return 0;
        // Compute EV axis for each line and also remember its book count for the guard.
        let bestIdx = 0;
        let bestEv = -Infinity;
        let fallbackIdx = 0;      // used only if no line clears minBooks
        let fallbackBooks = -1;
        // Ties (player-prop EV is capped at 35%) go to the line the results list
        // put first in groupedLines, so the card shows the line it was ranked by.
        const listRank = new Map();
        (groupedLines || []).forEach((l, i) => { if (!listRank.has(l)) listRank.set(l, i); });
        const rankOf = (l) => (listRank.has(l) ? listRank.get(l) : Infinity);
        for (let idx = 0; idx < lines.length; idx++) {
            const line = lines[idx];
            const r = line?.raw || line || {};
            const isSGPLeg = r.ev_method === 'individual_leg_devig';
            const bo = lineBookOdds(line);
            const bookKeys = Object.keys(bo).filter((bk) => !isUnbackableBookData(bo[bk], bk));
            const bookCount = cardBookCount(line);
            // Fallback line = most books overall (matches legacy picker)
            if (bookCount > fallbackBooks) { fallbackBooks = bookCount; fallbackIdx = idx; }
            if (bookCount < minBooks) continue;
            // Score = max cleanPerBookEv across the relevant book-set for this line.
            // If a myBooksNorm set is provided, only those books count; otherwise all trad books.
            let score = null;
            for (const bk of bookKeys) {
                if (myBooksNorm && !myBooksNorm.has(normBookName(bk))) continue;
                const wc = cleanPerBookEv(r, bo[bk], isSGPLeg, consensusOptions, bk);
                if (Number.isFinite(wc) && (score == null || wc > score)) score = wc;
            }
            if (score == null) continue;
            if (score > bestEv || (score === bestEv && rankOf(line) < rankOf(lines[bestIdx]))) { bestEv = score; bestIdx = idx; }
        }
        return bestEv > -Infinity ? bestIdx : fallbackIdx;
    };
    const workingOption = useMemo(() => {
        if (sortedLines && sortedLines.length > 0) {
            if (userHasSelectedRef.current) {
                return sortedLines[selectedLineIndex] || sortedLines[0];
            }
            // Compute optimal index synchronously (same logic as the auto-select effect)
            if (requestedLine !== null) {
                const targetLine = parseFloat(requestedLine);
                const direction = requestedDirection || 'over';
                let bestIdx = 0;
                let bestDist = Infinity;
                let bestInDirection = false;
                sortedLines.forEach((line, idx) => {
                    const lineVal = (lineNumberOf(line) ?? 0);
                    const dist = Math.abs(Math.abs(lineVal) - Math.abs(targetLine));
                    const inDirection = direction === 'over' ? lineVal >= targetLine : lineVal <= targetLine;
                    if (inDirection && !bestInDirection) {
                        bestIdx = idx; bestDist = dist; bestInDirection = true;
                    } else if (inDirection === bestInDirection && dist < bestDist) {
                        bestIdx = idx; bestDist = dist;
                    } else if (!bestInDirection && !inDirection && dist < bestDist) {
                        bestIdx = idx; bestDist = dist;
                    }
                });
                return sortedLines[bestIdx] || sortedLines[0];
            }
            // Explicit Additional Sorting: same pick as the auto-select effect.
            if (preserveLineOrder) return sortedLines[0];
            // v1.16.6 — Generic query (no requestedLine): DEFAULT TO HIGHEST-EV LINE.
            // v1.16.4 defaulted this to main line but that's wrong for generic
            // queries (user wants best edge, not the book's primary). Specific-
            // line queries still honor the exact line via the branch above.
            const _mbActive = sortBooksBy === 'mybooks' && Array.isArray(myBooks) && myBooks.length > 0;
            const _mbNorm = _mbActive ? new Set(myBooks.map(b => normBookName(b))) : null;
            const bestIdx = _pickBestEvLineIdx(sortedLines, { myBooksNorm: _mbNorm, minBooks: 5 });
            return sortedLines[bestIdx] || sortedLines[0];
        }
        if (groupedLines && groupedLines.length > 0) {
            return groupedLines[0];
        }
        return option;
    }, [option, sortedLines, groupedLines, selectedLineIndex, requestedLine, requestedDirection, sortBooksBy, myBooks, preserveLineOrder]);
    
    // Extract data from working option
    const raw = workingOption?.raw || {};
    const market = raw.market || workingOption?.title || '';
    const selection = sanitizeSelectionString(raw.selection || workingOption?.description || '');
    const isPlayerProp = isPlayerPropMarket(market);
    const isTeamWithAlts = isTeamPropWithAlts(market);
    const showMainAltBadge = isPlayerProp || isTeamWithAlts;
    
    // Book odds data - sorted based on sortBooksBy
    const bookOddsData = useMemo(() => {
        // ── GHOST FIX v1.15.10 ────────────────────────────────────────────────
        // Previous code used `workingOption?.bookOdds || raw.bookOdds || {}`,
        // but an empty object `{}` is TRUTHY in JS — so when `workingOption`
        // happened to be an alt line with no bookOdds (e.g. after sorting or
        // filtering picked a sparse line), the chain short-circuited on the
        // empty `{}` and `raw.bookOdds` (which carries the group-representative
        // 100+ books) was NEVER consulted. Result: card rendered as a ghost
        // with "0 Sportsbooks" even though the underlying data was rich.
        //
        // Fix: explicitly pick whichever source has actual entries, preferring
        // workingOption when populated, then raw, then workingOption.raw.
        const bookOdds = lineBookOdds(workingOption);
        // One deduped list for every count and price on the card (utils/cardBooks):
        // empty entries dropped, exchange lay feeds dropped (not backable),
        // regional variants ("BetMGM (UK)") folded into their brand at the best price.
        let entries = dedupeCardBooks(bookOdds).map(({ book, odds }) => ({ book, odds }));

        // Fallback: if bookOdds is empty but a single sportsbook is known,
        // synthesise a minimal entry so the card never shows "0 Sportsbooks"
        if (entries.length === 0) {
            const fallbackBook = workingOption?.raw?.sportsbook || raw.sportsbook ||
                                 workingOption?._book || raw._book ||
                                 workingOption?.book || raw.book || null;
            const fallbackPrice = workingOption?.raw?.price ?? raw.price ?? null;
            // Never resurrect an exchange lay feed as the card's one book.
            if (fallbackBook && !isLayBookName(fallbackBook)) {
                entries = [{ book: fallbackBook, odds: { price: fallbackPrice } }];
            }
        }
        
        // Sort based on user selection
        entries.sort((a, b) => {
            if (sortBooksBy === 'mybooks') {
                // My Books first, then by EV — use normalized matching to handle casing/punctuation differences
                const aInMyBooks = myBooks.some(mb => normBookName(mb) === normBookName(a.book));
                const bInMyBooks = myBooks.some(mb => normBookName(mb) === normBookName(b.book));
                if (aInMyBooks && !bInMyBooks) return -1;
                if (!aInMyBooks && bInMyBooks) return 1;
                // Secondary sort: best odds first (price-based, avoids circular dep with perBookEV)
                const priceA = extractPrice(a.odds);
                const priceB = extractPrice(b.odds);
                if (priceA === null) return 1;
                if (priceB === null) return -1;
                return priceB - priceA;
            } else if (sortBooksBy === 'name') {
                return a.book.localeCompare(b.book);
            } else if (sortBooksBy === 'ev') {
                // Sort by best price (= best EV) — perBookEV can't be used here since it's
                // declared after bookOddsData (hooks must be declared in order).
                // The display column still shows accurate per-book EV from perBookEV.
                const priceA = extractPrice(a.odds);
                const priceB = extractPrice(b.odds);
                if (priceA === null) return 1;
                if (priceB === null) return -1;
                return priceB - priceA; // Higher price = better odds = higher EV
            } else { // 'odds'
                const priceA = extractPrice(a.odds);
                const priceB = extractPrice(b.odds);
                if (priceA === null) return 1;
                if (priceB === null) return -1;
                return priceB - priceA;
            }
        });
        
        return entries;
    }, [workingOption, raw, sortBooksBy, myBooks]);
    
    const bookCount = bookOddsData.length;

    // ── GHOST-AUDIT v1.15.11 ───────────────────────────────────────────────
    // We instrument every UnifiedBetCard render so we can see the EXACT state
    // on first paint vs later paints (the ghost "fixes itself" when user opens
    // a filter — a parent re-render fixes the memo chain). We specifically want
    // to see:
    //   1. Is workingOption picking a sparse alt on render #1?
    //   2. Does the pick change on render #2 (after userProfile async hydrate)?
    //   3. Are myBooks / sortBooksBy / initialMinBooksFilter different between
    //      the two renders?
    //
    // To avoid console spam we log:
    //   • every card's render #1 and render #2 (always, always always)
    //   • any render where bookCount *transitions* (0→N or N→0)
    //   • ghost cases (bookCount===0 with groupedLines having data)
    const _renderCountRef = useRef(0);
    const _lastBookCountRef = useRef(null);
    _renderCountRef.current += 1;
    const _renderNum = _renderCountRef.current;
    const _prevBookCount = _lastBookCountRef.current;
    _lastBookCountRef.current = bookCount;
    const _isTransition = _prevBookCount !== null && _prevBookCount !== bookCount;
    const _isGhostCase = bookCount === 0 && Array.isArray(groupedLines) && groupedLines.length > 0;
    const _shouldLog = _renderNum <= 2 || _isTransition || _isGhostCase;
    if (_shouldLog) {
        try {
            /* eslint-disable no-console */
            const _woRef = workingOption;
            const _woIdx = Array.isArray(sortedLines) ? sortedLines.indexOf(_woRef) : -1;
            const _woBookKeys = Object.keys(_woRef?.bookOdds || {});
            const _woRawBookKeys = Object.keys(_woRef?.raw?.bookOdds || {});
            const _rawBookKeys = Object.keys(raw?.bookOdds || {});
            const _groupBookCounts = Array.isArray(groupedLines)
                ? groupedLines.map((l, i) => {
                      const boTop = l?.bookOdds || {};
                      const boRaw = l?.raw?.bookOdds || {};
                      return { i, topBooks: Object.keys(boTop).length, rawBooks: Object.keys(boRaw).length, sel: l?.raw?.selection || l?.selection };
                  })
                : null;
            const _tag = _isGhostCase ? 'GHOST' : (_isTransition ? 'TRANSITION' : `RENDER#${_renderNum}`);
            console.log(`[UnifiedBetCard ${_tag} v1.15.11]`, {
                cardKey: option?.id || raw.id || (raw.market + '|' + raw.selection),
                title: _woRef?.title || raw.market,
                selection: raw.selection || _woRef?.selection,
                renderNum: _renderNum,
                bookCount,
                prevBookCount: _prevBookCount,
                groupedLinesLen: Array.isArray(groupedLines) ? groupedLines.length : null,
                sortedLinesLen: sortedLines?.length ?? null,
                workingOptionIdxInSorted: _woIdx,
                workingOptionTopBookKeys: _woBookKeys.length,
                workingOptionRawBookKeys: _woRawBookKeys.length,
                rawBookKeys: _rawBookKeys.length,
                selectedLineIndex,
                minBooksFilter,
                initialMinBooksFilterProp: initialMinBooksFilter,
                sortBooksBy,
                defaultBookSortProp: defaultBookSort,
                myBooksLen: Array.isArray(myBooks) ? myBooks.length : 0,
                showMainOnly,
                userHasSelected: userHasSelectedRef.current,
                isSGP: !!(_woRef?._isCachedSGP || raw._isCachedSGP),
                groupLineBookCounts: _groupBookCounts,
            });
            /* eslint-enable no-console */
        } catch { /* no-op */ }
    }
    // When an active filter is on, show how many of the filtered books offer this line
    const filteredBookCount = activeBookFilter.length > 0
        ? bookOddsData.filter(b => activeBookFilter.some(f =>
            (b.book || '').toLowerCase().replace(/[^a-z0-9]/g, '') ===
            f.toLowerCase().replace(/[^a-z0-9]/g, '')
          )).length
        : bookCount;
    const displayedBooks = showAllBooks ? bookOddsData : bookOddsData.slice(0, 6);
    
    // Best available price across ALL books (independent of sort order — avoids circular dependency)
    // Used as the basis for devigMethods and perBookEV when no specific book is selected
    const bestRawPrice = useMemo(() => {
        // v1.15.10 ghost fix: pick first non-empty source instead of naive `||` chain
        // (an empty object is truthy and would incorrectly short-circuit the chain).
        const _src =
            (raw?.bookOdds && Object.keys(raw.bookOdds).length > 0 && raw.bookOdds) ||
            (workingOption?.bookOdds && Object.keys(workingOption.bookOdds).length > 0 && workingOption.bookOdds) ||
            (workingOption?.raw?.bookOdds && Object.keys(workingOption.raw.bookOdds).length > 0 && workingOption.raw.bookOdds) ||
            {};
        const allPrices = Object.entries(_src)
            .filter(([b, o]) => !isUnbackableBookData(o, b))
            .map(([, o]) => extractPrice(o))
            .filter(p => p !== null && typeof p === 'number');
        if (allPrices.length === 0) return null;
        return allPrices.reduce((best, p) => (best === null || p > best) ? p : best, null);
    }, [raw.bookOdds, workingOption]);
    
    // Price and probability calculations
    const price = useMemo(() => {
        if (selectedBook === 'best') {
            // Use the true best available price, not bookOddsData[0] (which depends on sort order)
            return bestRawPrice ?? (bookOddsData.length > 0 ? extractPrice(bookOddsData[0].odds) : null);
        }
        const bookData = bookOddsData.find(b => b.book === selectedBook);
        return bookData ? extractPrice(bookData.odds) : null;
    }, [selectedBook, bestRawPrice, bookOddsData]);
    
    const implied = getImpliedProbability(price);
    
    // EV calculation — use the minimum of reliable devig methods (most conservative).
    // SGPs use the R-factor correlated fair value (computed in sgpOptimizer), which
    // correctly accounts for leg correlation via market-derived R = legProduct / avgSGPDecimal.
    // Additive is excluded for SGPs (breaks for extreme correlated longshots).
    // Falls back to raw.ev_percent (already the backend-computed conservative value).
    const ev = useMemo(() => {
        // Server-marked no-EV row (functions/utils/noEvRows): no EV, no Kelly.
        if (isNoEvRow(raw) || isNoEvRow(workingOption)) return null;
        // PRIORITY 0: Cached SGPs — same path as sort axis (cleanPerBookEv / betMath core).
        if (workingOption?._isCachedSGP) {
            const bookOddsMap = raw.bookOdds || workingOption?.bookOdds || {};
            const selPrice = selectedBook !== 'best'
                ? (bookOddsMap[selectedBook]?.price ?? null)
                : (raw.combined_price ?? null);
            if (selPrice != null) {
                const pillEv = cleanPerBookEv(raw, bookOddsMap[selectedBook] ?? { price: selPrice }, true, consensusOptions, bookOddsMap[selectedBook] ? selectedBook : null);
                if (Number.isFinite(pillEv)) return parseFloat(pillEv.toFixed(2));
            }
            // Last-resort fallbacks when method stamps or bookOdds are missing.
            if (selectedBook !== 'best') {
                const bData = bookOddsMap[selectedBook];
                if (bData?.ev_percent != null) return bData.ev_percent;
            }
            const vals = [raw.ev_method1_additive, raw.ev_method2_multiplicative, raw.ev_method3_power]
                .filter((v) => v != null);
            if (vals.length > 0) return Math.min(...vals);
            return raw.ev_percent ?? workingOption?.ev_percent ?? null;
        }
        
        // PRIORITY 1: Live SGPs (from API with ev_method set)
        const isSGP = raw.ev_method === 'individual_leg_devig';

        // ⭐ PILL/SORT PARITY: When a specific book is selected, compute EV via the
        // SAME `cleanPerBookEv` function the results-list sort uses. This guarantees
        // the single number in the header pill ≡ the sort axis. Previously this read
        // `bookData.odds.ev_percent` (a legacy per-book stamp) which diverges from
        // backend's newer `clean_ev_percent` stamp (authoritative leave-one-out
        // consensus) — so the sort could rank card A above card B while the pill
        // showed card A with a WORSE EV than card B. That made the list look
        // scrambled. Using cleanPerBookEv here pulls the same stamp the sort uses.
        if (selectedBook !== 'best') {
            const bookData = bookOddsData.find(b => b.book === selectedBook);
            if (bookData?.odds) {
                const cpe = cleanPerBookEv(raw, bookData.odds, isSGP, consensusOptions, selectedBook);
                if (Number.isFinite(cpe)) return parseFloat(cpe.toFixed(2));
                // Fallback to legacy per-book stamp if cleanPerBookEv can't resolve
                // (e.g. very old cache entries with no clean_ev_percent / consensus).
                if (bookData.odds.ev_percent !== undefined) return bookData.odds.ev_percent;
            }
        }
        // ⭐ v1.18 P1 additive alignment: min(add, mult, pow) unconditionally —
        // the SAME min-set as canonicalEv.wcEvFromMethods (the sort axis and
        // per-book path). The old 15pp "additive outlier" guard made this
        // fallback pill LESS conservative than the sort for exactly the rows
        // where additive flagged a problem — pill and list order could disagree.
        // Including additive only ever lowers the shown EV (conservative-safe).
        const m1 = raw.ev_method1_additive;
        const m2 = raw.ev_method2_multiplicative;
        const m3 = raw.ev_method3_power;
        // Assumed vig (one-sided): additive out, power a veto only (floored at
        // 0%) — the same rule as the pill / sort (betMath.powerEvForMinSet).
        if (!isSGP && raw.devig_methods?.devig_source === 'estimated') {
            const p3 = m3 != null
                ? betMath.powerEvForMinSet(m3, { estimatedVig: betMath.isAssumedVigConsensus(raw.devig_methods), multiplicativeEv: m2 })
                : null;
            const est = [m2, p3].filter(v => v !== null && v !== undefined);
            if (est.length > 0) return Math.min(...est);
        }
        const wcValids = [m1, m2, m3].filter(v => v !== null && v !== undefined);
        if (wcValids.length > 0) return Math.min(...wcValids);
        return raw.ev_percent ?? workingOption?.ev_percent ?? null;
    }, [selectedBook, bookOddsData, raw, workingOption, implied]);
    
    // ⭐ THREE EV DEVIGGING METHODS — shared with results sort (../utils/canonicalEv.js)
    // For cached SGPs: use the backend-stored EV values directly (R-factor correlated).
    // These already account for leg correlation via the R-factor = legProduct / avgSGPDecimal.
    // Live devigging would ignore correlation and produce wrong values.
    // For everything else: compute live via computeDevigMethodsSnapshot.
    const devigMethods = useMemo(() => {
        if (isNoEvRow(raw) || isNoEvRow(workingOption)) return null; // no math block for a no-EV row
        if (workingOption?._isCachedSGP) {
            const bookOddsMap = raw.bookOdds || workingOption?.bookOdds || {};
            const bestPrice = raw.combined_price;
            let selBookData = selectedBook !== 'best' ? bookOddsMap[selectedBook] : null;
            const selPrice = selBookData?.price ?? bestPrice;
            if (!selBookData && typeof selPrice === 'number') {
                selBookData = Object.values(bookOddsMap).find((bd) => bd?.price === selPrice) ?? null;
            }
            const snapshot = sgpDevigMethodsSnapshot(raw, selPrice, consensusOptions, selBookData);
            if (snapshot) return snapshot;
        }

        // ⭐ STRAIGHT BETS — unified math using top-level consensus fair probs
        //
        // The backend stores `consensus_fair_{add,mult,pow}` on each item, representing
        // the leave-one-out peer-averaged fair probability for this outcome. This is the
        // book-agnostic source of truth. Per-book stored ev_method* values are currently
        // corrupt for ~60% of entries (bogus `opposite_american_odds` placeholders
        // produce clamped/identical method values), so we compute every book's EV on
        // the frontend as: `consensus_fair × book_decimal − 1`.
        //
        // Display semantics: the card's EV is computed against the currently selected
        // book's price. When no book is selected ('best'), we use the highest-priced
        // book — which is also the best-EV book since EV is monotonic in price under a
        // uniform fair prob.
        const bookOddsMap = raw.bookOdds || workingOption?.bookOdds || {};

        // Resolve selected book + its price + its PER-BOOK consensus fair (leave-one-out
        // excluding THAT book's own juice). This is the same source the backend uses to
        // stamp `bookOdds[bk].clean_ev_percent` — by using it here too, the math block's
        // per-method rows and the header pill's "clean EV" display are always computed
        // from identical inputs, so "✓ Used" always numerically matches the pill.
        let selPrice = null;
        let selBookData = null;
        if (selectedBook && selectedBook !== 'best') {
            const bd = bookOddsMap[selectedBook];
            selBookData = (typeof bd === 'object' && bd !== null) ? bd : null;
            selPrice = typeof bd === 'object' ? bd?.price : bd;
        }
        if (selPrice == null) {
            // 'best' or missing selected-book → use highest-priced book (also uses its own dm)
            let topBd = null;
            Object.entries(bookOddsMap).forEach(([bk, bd]) => {
                if (isUnbackableBookData(bd, bk)) return;
                const p = typeof bd === 'object' ? bd?.price : bd;
                if (typeof p === 'number' && (selPrice == null || p > selPrice)) {
                    selPrice = p;
                    topBd = (typeof bd === 'object' && bd !== null) ? bd : null;
                }
            });
            selBookData = topBd;
            if (selPrice == null) selPrice = price;
        }

        // Prefer per-book consensus fair (leave-one-out, excludes the priced book). Fall
        // back to item-level consensus, then to same-book devig probs.
        const perBookDm = selBookData?.devig_methods || null;
        const itemDm = raw?.devig_methods || null;
        const pickFair = (key, fallbackKey) =>
            (perBookDm && Number.isFinite(perBookDm[key])) ? perBookDm[key]
            : (itemDm && Number.isFinite(itemDm[key])) ? itemDm[key]
            : (perBookDm && fallbackKey && Number.isFinite(perBookDm[fallbackKey])) ? perBookDm[fallbackKey]
            : (itemDm && fallbackKey && Number.isFinite(itemDm[fallbackKey])) ? itemDm[fallbackKey]
            : null;

        const fairAdd  = pickFair('consensus_fair_add',  'additive_prob');
        const fairMult = pickFair('consensus_fair_mult', 'multiplicative_prob');
        const fairPow  = pickFair('consensus_fair_pow',  'power_prob');
        const haveAny = [fairAdd, fairMult, fairPow].some(v => Number.isFinite(v));

        const stampedRows = straightDevigMethodsSnapshot(selBookData, consensusOptions);
        if (stampedRows) return stampedRows;

        if (haveAny && typeof selPrice === 'number') {
            const dec = betMath.americanToDecimal(selPrice);
            const calcEV = (fp) => {
                const ev = betMath.evPercent(fp, dec);
                return ev != null ? parseFloat(ev.toFixed(2)) : null;
            };
            return {
                additive:       fairAdd  != null ? { fairProb: fairAdd,  ev: calcEV(fairAdd)  } : null,
                multiplicative: fairMult != null ? { fairProb: fairMult, ev: calcEV(fairMult) } : null,
                power:          fairPow  != null ? { fairProb: fairPow,  ev: calcEV(fairPow)  } : null,
                methodUsed: raw.ev_method_used || 'power',
                isSGP: false,
            };
        }

        // Last-resort fallback (no consensus data at all) — legacy devig snapshot
        return computeDevigMethodsSnapshot(workingOption, marketPeers || [], price, selBookData);
    }, [workingOption, marketPeers, price, raw, selectedBook, consensusOptions]);
    
    // ⭐ Per-book EV — one worst-case EV per book for panel display + sorting
    // Uses the same authoritative math as the card badge: consensus fair prob × book decimal.
    const perBookEVRaw = useMemo(() => {
        const isSGP = devigMethods?.isSGP ?? false;
        const sourceOdds = raw.bookOdds || workingOption?.bookOdds || {};
        const result = {};

        // ⭐ PILL↔MATH PARITY for SGPs (v1.13.x): recompute per-book EV from the
        // SAME three method values the math block renders (ev_method1_additive,
        // ev_method2_multiplicative, ev_method3_power) at each book's price,
        // then take MIN across all three. Additive is INCLUDED per client
        // preference — the extra conservatism on extreme-correlation SGPs is
        // desirable (overcorrect so a "too good" parlay stops ranking as a best
        // bet). See `ev` memo above for the full rationale.
        if (isSGP) {
            // Canonical SGP per-book EV — SAME LOO cleanPerBookEv path used by the
            // sort axis (getCanonicalSortEv → fallbackEvLikeCardEvMemo) and the
            // BetCardV2 pill (betCardV2Summary.extractSummary), so rail ≡ pill ≡ sort.
            // (v1.18 P0 fix: this branch previously called sgpEvAtBookPrice, which does
            // NOT apply leave-one-out — so the v1 card rail disagreed with the v2 pill
            // and the list position for any correlated SGP. Routing through
            // cleanPerBookEv unifies all three surfaces on the LOO formula.)
            Object.entries(sourceOdds).forEach(([book, odds]) => {
                const bp = typeof odds === 'object' ? odds?.price : odds;
                if (typeof bp !== 'number' || !Number.isFinite(bp)) return;
                const wc = cleanPerBookEv(raw, odds, true, consensusOptions, book);
                if (wc != null) {
                    result[book] = parseFloat(wc.toFixed(1));
                } else if (odds?.ev_percent != null) {
                    result[book] = odds.ev_percent;
                }
            });
            if (Object.keys(result).length > 0) return result;
            // Legacy fallback: stored per-book ev_percent when methods are missing
            Object.entries(sourceOdds).forEach(([book, odds]) => {
                if (odds?.ev_percent != null) result[book] = odds.ev_percent;
            });
            if (Object.keys(result).length > 0) return result;
        }

        // ⭐ Non-SGP straight-bet path: use the same fair probs the math block resolved.
        // This handles player-prop items that carry devig_methods at the top level of the
        // result item (not nested under a .raw property), which causes raw = {} and makes
        // cleanPerBookEv fail all four priorities. devigMethods already resolved the correct
        // consensus fair probs — reuse them so the books panel matches the math block.
        const fp1 = devigMethods?.additive?.fairProb ?? null;
        const fp2 = devigMethods?.multiplicative?.fairProb ?? null;
        const fp3 = devigMethods?.power?.fairProb ?? null;
        if (fp1 != null || fp2 != null || fp3 != null) {
            Object.entries(sourceOdds).forEach(([book, odds]) => {
                const bp = typeof odds === 'object' ? odds?.price : odds;
                if (typeof bp !== 'number') return;
                const dec = betMath.americanToDecimal(bp);
                const ev1 = fp1 != null ? betMath.evPercent(fp1, dec) : null;
                const ev2 = fp2 != null ? betMath.evPercent(fp2, dec) : null;
                const ev3 = fp3 != null ? betMath.evPercent(fp3, dec) : null;
                const vals = [ev1, ev2, ev3].filter(v => v != null);
                if (vals.length > 0) result[book] = parseFloat(Math.min(...vals).toFixed(1));
            });
            if (Object.keys(result).length > 0) return result;
        }

        // Final fallback: cleanPerBookEv (works for main-line cache items that have a
        // proper nested .raw with devig_methods / clean_ev_percent per-book).
        Object.entries(sourceOdds).forEach(([book, odds]) => {
            const wc = cleanPerBookEv(raw, odds, isSGP, consensusOptions, book);
            if (wc != null) result[book] = parseFloat(wc.toFixed(1));
        });
        return result;
    }, [devigMethods, raw, workingOption, consensusOptions]);
    // Books the backend flagged as single-book price outliers (stale quotes,
    // evCleanPerBook.flagPriceOutliers) get no EV: they sort last and are never
    // auto-selected, so the card can't open on a phantom edge.
    const perBookEV = useMemo(() => {
        const bo = raw.bookOdds || workingOption?.bookOdds || {};
        if (!Object.values(bo).some(isUnbackableBookData)) return perBookEVRaw;
        const out = { ...perBookEVRaw };
        for (const [book, bd] of Object.entries(bo)) if (isUnbackableBookData(bd, book)) out[book] = null;
        return out;
    }, [perBookEVRaw, raw, workingOption]);
    
    // sortedBookOddsData: re-sort bookOddsData by actual per-book EV when sortBooksBy === 'ev' or 'mybooks'
    // (bookOddsData can't reference perBookEV due to hook declaration order, so we sort here after perBookEV is ready)
    const sortedBookOddsData = useMemo(() => {
        if (sortBooksBy !== 'ev' && sortBooksBy !== 'mybooks') return bookOddsData;
        return [...bookOddsData].sort((a, b) => {
            const evA = perBookEV[a.book] ?? null;
            const evB = perBookEV[b.book] ?? null;
            if (evA === null && evB === null) return 0;
            if (evA === null) return 1;
            if (evB === null) return -1;
            return evB - evA; // Higher EV first
        });
    }, [bookOddsData, perBookEV, sortBooksBy]);
    const myBooksFilteredData = useMemo(() => {
        if (sortBooksBy !== 'mybooks' || myBooks.length === 0) return sortedBookOddsData;
        return sortedBookOddsData.filter(item =>
            myBooks.some(mb => normBookName(mb) === normBookName(item.book))
        );
    }, [sortedBookOddsData, sortBooksBy, myBooks]);
    const sortedDisplayedBooks = showAllBooks ? myBooksFilteredData : myBooksFilteredData.slice(0, 6);

    // ⭐ AUTO-SELECT TOP BOOK: runs once when sorted data is first ready, or when the option changes.
    // Picks the top book from the relevant sorted list so the card always opens on the best choice.
    // Never overrides a manual user selection (userHasSelectedRef).
    const _autoBookSelectDoneRef = useRef(false);
    // Reset auto-select when the underlying option changes (new query result or different fixture/market).
    useEffect(() => {
        _autoBookSelectDoneRef.current = false;
    }, [raw?.fixture_id, raw?.market, raw?.selection, raw?.line]);
    // ⭐ Re-snap highlight when the sidebar's sort axis flips (EV ↔ My Books).
    // The two axes are independent — each produces a different #1 book — so the
    // green check has to move to match whichever list the user is now looking at.
    // Manual user selections (userHasSelectedRef) are preserved: deliberate clicks
    // beat the auto-snap.
    useEffect(() => {
        if (userHasSelectedRef.current) return;
        _autoBookSelectDoneRef.current = false;
    }, [sortBooksBy]);
    // ⭐ Sync `sortBooksBy` with the `defaultBookSort` prop when it changes (e.g.
    // preferences hydrate async → parent flips prop from 'ev' → 'mybooks'). Without
    // this, useState(defaultBookSort) only fires on mount and the books panel is
    // stuck on the initial axis forever — which is why auto-snap kept picking
    // global top-EV outliers (Prophet X / Novig / BetOnline) even when My Books
    // mode was active. Honor manual user sort overrides via userHasSelectedSortRef.
    useEffect(() => {
        if (userHasSelectedSortRef.current) return;
        if (defaultBookSort && defaultBookSort !== sortBooksBy) {
            setSortBooksBy(defaultBookSort);
        }
    }, [defaultBookSort, sortBooksBy]);

    useEffect(() => {
        if (_autoBookSelectDoneRef.current) return;
        if (userHasSelectedRef.current) return;

        // Always resolve "top of current sort" first — that's the row the sidebar
        // renders at the top, and the highlighted row should match it so users
        // never see the green check land on a book that isn't visually #1.
        const targetList = (sortBooksBy === 'mybooks' && myBooksFilteredData.length > 0)
            ? myBooksFilteredData
            : sortedBookOddsData;
        const topWithEV = targetList.find(x => perBookEV[x.book] != null);
        const topBook = (topWithEV?.book) || targetList[0]?.book;

        // Respect an explicit `defaultSelectedBook` (e.g. the user queried
        // "Thunder on FanDuel") only when `highlightPreferredBook` is set —
        // that's the signal the hint came from a real user intent and isn't
        // just leftover state from a previous sort.
        //
        // Explicit query intent beats both sort axes: search the FULL book list
        // (not just myBooksFilteredData) so a queried book like FanDuel still
        // auto-selects even when the user's default sort is My Books and their
        // My Books list doesn't include FanDuel. Use normalized matching so
        // minor casing/punctuation differences between the query-derived name
        // and the bookOdds key don't drop the match.
        if (defaultSelectedBook && highlightPreferredBook) {
            const wantNorm = normBookName(defaultSelectedBook);
            const hit = sortedBookOddsData.find(x => normBookName(x.book) === wantNorm);
            if (hit) {
                setSelectedBook(hit.book); // use the exact key from bookOddsData
                _autoBookSelectDoneRef.current = true;
                return;
            }
        }

        if (topBook) {
            setSelectedBook(topBook);
            _autoBookSelectDoneRef.current = true;
        }
    }, [sortedBookOddsData, myBooksFilteredData, defaultSelectedBook, sortBooksBy, highlightPreferredBook]);

    // Kelly criterion — ⭐ v1.18 P1: Kelly and the EV pill share ONE fair prob.
    //
    // The fair prob is derived FROM the pill EV at the displayed price:
    //   p = (ev/100 + 1) / decimal
    // Since `ev` is the conservative min-of-methods number computed at this same
    // price (cleanPerBookEv / method stamps), this inversion recovers exactly the
    // fair probability that produced the pill — so a +EV pill can never coexist
    // with a 0% Kelly, and a −EV pill always gives Kelly 0.
    //
    // Previous behavior used ai_prob / math_prob / SGP fair_probability / a crude
    // 10%-vig strip — a DIFFERENT fair prob than the pill (fair_probability uses
    // per-leg min(mult,pow), the vig strip ignores consensus entirely), which is
    // why users could see "+4% EV" next to "Kelly 0.0%".
    const pillFairProb = useMemo(() => {
        if (typeof ev !== 'number' || !Number.isFinite(ev) || price == null) return null;
        const dec = betMath.americanToDecimal(price);
        if (!dec) return null;
        const p = betMath.fairProbFromEv(ev, dec);
        return (p != null && p > 0 && p < 1) ? p : null;
    }, [ev, price]);
    // Legacy fallback chain — only used when the pill EV itself is unavailable
    // (no method stamps, no consensus, no per-book stamps: very old cache rows).
    const _sgpFairProb = (isSGP && raw.fair_probability != null)
        ? Math.max(0.001, Math.min(0.99, raw.fair_probability / 100))
        : null;
    const aiProb = raw.ai_prob || raw.math_prob || _sgpFairProb || (() => {
        if (!implied || implied <= 0 || implied >= 1) return null;
        const vig = 0.10;
        const opp = Math.max(0.01, Math.min(0.99, (1 + vig) - implied));
        const totalImp = implied + opp;
        if (totalImp <= 1) return null;
        const vigAmt = totalImp - 1;
        const adj = 1 + (vigAmt * (1 - implied));
        return adj > 0 ? Math.max(0.01, Math.min(0.99, implied / adj)) : null;
    })();
    const kellyUnits = useMemo(() => {
        const p = pillFairProb ?? aiProb;
        if (p === null || price === null) return null;
        return betMath.kellyFraction(p, price, kellyCriterion);
    }, [pillFairProb, aiProb, price, kellyCriterion]);
    
    const suggestedWager = kellyUnits !== null && kellyUnits > 0 ? Math.max(1, Math.round(stake * kellyUnits)) : null;
    const payout = price !== null ? (price > 0 ? stake * (price / 100) + stake : stake * (100 / Math.abs(price)) + stake) : null;

    // ─── Consensus inclusion set (v1.16.x) ────────────────────────────────────
    // Derived from _rawBookConsensus (backend-stamped, P3.1). Any book NOT in
    // this set was filtered out of the devig consensus upstream (DFS / sweeps /
    // regional mirror / no opposite side / way-outlier price / etc). We only
    // trust this signal when _rawBookConsensus is present and non-empty — for
    // legacy / SGP / pre-v1.15 rows we fall back to "no info" (undefined) so
    // we don't false-flag every book as excluded.
    const consensusIncludedSet = useMemo(() => {
        const arr = Array.isArray(raw?._rawBookConsensus) ? raw._rawBookConsensus : null;
        if (!arr || arr.length === 0) return null;
        const s = new Set();
        for (const r of arr) {
            if (r && typeof r.book === 'string' && r.book.length > 0) {
                s.add(normBookName(r.book));
            }
        }
        return s.size > 0 ? s : null;
    }, [raw?._rawBookConsensus]);

    // ─── Dev/admin "copy all books" ───────────────────────────────────────────
    // Lets an admin (or local dev build) grab a plain-text dump of every book
    // on the card — odds, implied, EV, and consensus-inclusion status — so we
    // can debug line-matching / exclusion issues without screenshots.
    const [copyFlash, setCopyFlash] = useState(false);
    const canShowDevCopy = isAdmin === true || (typeof __DEV__ !== 'undefined' && __DEV__);

    const buildBookDump = useCallback(() => {
        const market = raw?.market || workingOption?.title || '';
        const selection = raw?.selection || workingOption?.description || '';
        const player = raw?.player_name || '';
        const away = raw?.away_team || '';
        const home = raw?.home_team || '';
        const commence = raw?.commence_time || workingOption?.raw?.commence_time || '';
        const fixtureId = raw?.fixture_id || '';
        const leagueLabel = raw?.league || raw?.sport || '';

        // Dump EVERY book we have data for, not just the first page of the
        // collapsed sidebar. bookOddsData is the full, EV-sorted list.
        const rows = (bookOddsData || []).map((item) => {
            const bk = item.book;
            const p = extractPrice(item.odds);
            const imp = typeof p === 'number' && p !== 0
                ? (p > 0 ? 100 / (p + 100) : Math.abs(p) / (Math.abs(p) + 100))
                : null;
            const ev = perBookEV[bk] ?? item.odds?.ev_percent;
            const norm = normBookName(bk);
            const userExcluded = consensusPolicy === 'custom' && excludedBookSet && excludedBookSet.has(String(bk).toLowerCase());
            let status;
            if (userExcluded) status = 'excluded_by_user';
            else if (consensusIncludedSet) status = consensusIncludedSet.has(norm) ? 'in_consensus' : 'excluded_backend';
            else status = 'unknown';
            return { bk, p, imp, ev, status };
        });

        // Column widths chosen for ~100-char terminal. Truncate long book names.
        const lines = [];
        const title = [market, selection].filter(Boolean).join(' · ');
        if (title) lines.push(title);
        if (player) lines.push(`Player: ${player}`);
        if (away || home) lines.push(`${away || 'TBD'} @ ${home || 'TBD'}`);
        if (commence) lines.push(`Commence: ${commence}`);
        if (fixtureId) lines.push(`Fixture: ${fixtureId}${leagueLabel ? ` · ${leagueLabel}` : ''}`);
        lines.push('');
        lines.push('Book                              Odds     Implied  EV        Status');
        lines.push('──────────────────────────────────────────────────────────────────────────');
        for (const r of rows) {
            const bkCol  = (r.bk || '').slice(0, 32).padEnd(32, ' ');
            const oddsCol = (r.p != null ? (r.p > 0 ? `+${r.p}` : String(r.p)) : '—').padEnd(8, ' ');
            const impCol  = (r.imp != null ? `${(r.imp * 100).toFixed(2)}%` : '—').padEnd(8, ' ');
            const evCol   = (Number.isFinite(r.ev) ? `${r.ev >= 0 ? '+' : ''}${r.ev.toFixed(2)}%` : '—').padEnd(9, ' ');
            lines.push(`${bkCol}  ${oddsCol} ${impCol} ${evCol} ${r.status}`);
        }
        lines.push('');

        // Consensus + diagnostics block
        const dm = raw?.devig_methods || null;
        const cfAdd  = dm?.consensus_fair_add  ?? null;
        const cfMult = dm?.consensus_fair_mult ?? null;
        const cfPow  = dm?.consensus_fair_pow  ?? null;
        const ti     = dm?.total_implied       ?? null;
        const vig    = dm?.vig_percent         ?? null;
        const nBooks = dm?.consensus_book_count ?? null;
        const nOpp   = dm?.opposite_book_count  ?? null;
        const evSrc  = raw?._ev_source || dm?.ev_source || '—';
        const fpToAm = (fp) => (fp != null && fp > 0 && fp < 1)
            ? (fp > 0.5 ? `-${Math.round((fp / (1 - fp)) * 100)}` : `+${Math.round(((1 - fp) / fp) * 100)}`)
            : '—';
        lines.push(`Consensus fair:   add=${fpToAm(cfAdd)} (${cfAdd != null ? (cfAdd*100).toFixed(1)+'%' : '—'}) · mult=${fpToAm(cfMult)} · pow=${fpToAm(cfPow)}`);
        lines.push(`Total implied:    ${ti != null ? ti.toFixed(4) : '—'}${vig != null ? ` (vig ${(vig*100).toFixed(2)}%)` : ''}`);
        lines.push(`Books:            ${nBooks ?? '—'} in consensus · ${nOpp ?? '—'} with opposite side · ${rows.length} total on card`);
        lines.push(`Ev source:        ${evSrc}`);
        lines.push(`Consensus policy: ${consensusPolicy || 'default'}${excludedBookSet && excludedBookSet.size > 0 ? ` · ${excludedBookSet.size} books user-excluded` : ''}`);
        if (raw?.r_factor != null) {
            lines.push(`R-factor:         ${Number(raw.r_factor).toFixed(3)}${raw?.r_factor_clamped ? ' (clamped)' : ''}`);
        }

        return lines.join('\n');
    }, [raw, workingOption, bookOddsData, perBookEV, consensusIncludedSet, consensusPolicy, excludedBookSet]);

    const handleCopyBooks = useCallback(async () => {
        const text = buildBookDump();
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Non-web runtime without web clipboard — surface the dump in the
                // console so a dev can still grab it from DevTools / Metro logs.
                console.log('[UnifiedBetCard] clipboard unavailable, dump:\n' + text);
            }
            setCopyFlash(true);
            setTimeout(() => setCopyFlash(false), 1200);
        } catch (e) {
            console.warn('[UnifiedBetCard] copy failed:', e?.message || e);
            console.log('[UnifiedBetCard] dump:\n' + text);
        }
    }, [buildBookDump]);

    // Market average — average in implied-probability space, then convert back to American.
    // Direct arithmetic mean of American odds is wrong because American odds are not linear:
    // (-800 + +750) / 2 = -25, but the true market midpoint is near -101 (both sides ~50%).
    // Correct method: convert each price → implied probability, average those, convert back.
    const marketAverage = useMemo(() => {
        if (bookOddsData.length === 0) return null;
        // When a specific book is selected, exclude it from the market average
        // so Mkt Avg represents the consensus of all OTHER books
        const booksForAvg = selectedBook !== 'best'
            ? bookOddsData.filter(b => b.book !== selectedBook)
            : bookOddsData;
        const source = booksForAvg.length > 0 ? booksForAvg : bookOddsData;
        // Even money (every book +100) is +100, not -100 (audit Oct 26).
        return marketAverageAmerican(source.map(b => extractPrice(b.odds)));
    }, [bookOddsData, selectedBook]);

    // Consensus fair value — pre-computed per-book in the cache (excl. that book's own vig bias).
    // Stored as consensus_fair_add/mult/pow in each bookOdds[book].devig_methods by oddsRefresh.js.
    // Must run before fairPrice so the math row matches the same numbers as EV (overrideWithConsensusFairProbs).
    const consensusFairData = useMemo(() => {
        const fpToAmerican = (fp) => {
            if (!fp || fp <= 0 || fp >= 1) return null;
            return fp > 0.5 ? -Math.round((fp / (1 - fp)) * 100) : Math.round(((1 - fp) / fp) * 100);
        };
        let dm = null;
        if (selectedBook !== 'best') {
            const bookEntry = bookOddsData.find(b => b.book === selectedBook);
            dm = bookEntry?.odds?.devig_methods;
        }
        if (!dm) dm = raw.devig_methods;
        if (!dm) return null;
        if (isNoEvRow(raw)) return null;

        const cfAdd  = dm.consensus_fair_add  ?? null;
        const cfMult = dm.consensus_fair_mult ?? null;
        const cfPow  = dm.consensus_fair_pow  ?? null;
        if (!cfPow && !cfMult && !cfAdd) return null;

        // Conservative (worst-case) consensus fair prob — same method selection as EV.
        // Assumed vig: power is a veto only, never the fair estimate (its fair
        // collapses on longshots), so the fair shown is the multiplicative one.
        const fairProb = (betMath.isAssumedVigConsensus(dm) && cfMult != null)
            ? cfMult
            : (cfPow ?? cfMult ?? cfAdd);
        const bookCount = dm.consensus_book_count ?? null;
        const bookLabel = selectedBook !== 'best' ? selectedBook : (raw.sportsbook || null);

        return {
            add: cfAdd,
            mult: cfMult,
            pow: cfPow,
            fairProb,
            fairPriceAmerican: fpToAmerican(fairProb),
            bookCount,
            bookLabel,
        };
    }, [selectedBook, bookOddsData, raw]);

    // Fair price (American): for cached SGPs use backend fair_price directly;
    // for straights use consensus fair prob when present, else per-row devig from devigMethods.
    const fairPrice = useMemo(() => {
        if (workingOption?._isCachedSGP && raw.fair_price != null) {
            return raw.fair_price;
        }
        if (consensusFairData?.fairProb != null) {
            return consensusFairData.fairPriceAmerican;
        }
        if (!devigMethods) return null;
        const fp = devigMethods.power?.fairProb ?? devigMethods.multiplicative?.fairProb ?? devigMethods.additive?.fairProb;
        if (!fp || fp <= 0 || fp >= 1) return null;
        if (fp >= 0.5) return -Math.round((fp / (1 - fp)) * 100);
        return Math.round(((1 - fp) / fp) * 100);
    }, [consensusFairData, devigMethods, workingOption, raw]);

    // Cross-book consensus is often absent on SGP payloads — fall back to the same fairProb that drives EV / Fair Price (+331).
    const effectiveFairProb = useMemo(() => {
        if (consensusFairData?.fairProb != null) return consensusFairData.fairProb;
        const fp =
            devigMethods?.power?.fairProb ??
            devigMethods?.multiplicative?.fairProb ??
            devigMethods?.additive?.fairProb;
        return fp != null && fp > 0 && fp < 1 ? fp : null;
    }, [consensusFairData, devigMethods]);

    // ═══════════════════════════════════════════════════════════════════════════
    // ⭐ DATE/TIME & STATUS - Like the ticker, compute from actual game time
    // ═══════════════════════════════════════════════════════════════════════════
    
    // Every date field the pipelines write, then the ticker's fixture (by id, then teams):
    // cards said "Time TBD" for games whose kickoff the ticker already knew (audit Oct 26).
    const tickerCtx = useContext(TickerContext);
    const tickerFixtures = tickerCtx?.fixtures;
    const gameDate = useMemo(
        () => resolveCardKickoff(raw, workingOption, tickerFixtures),
        [raw, workingOption, tickerFixtures]
    );
    
    // ⭐ Compute game status (LIVE, SOON, UPCOMING, ENDED) like the ticker
    const gameStatus = useMemo(() => {
        if (!gameDate) {
            return { status: 'unknown', label: null, color: '#6B7280', icon: 'help-circle-outline' };
        }
        
        // Freeze mode: the snapshot's clock, not today (Oct 1, 2026: every frozen
        // card said FINAL while the ticker above said UPCOMING).
        const refMs = referenceTime ? new Date(referenceTime).getTime() : NaN;
        const now = Number.isFinite(refMs) ? new Date(refMs) : new Date();
        const diffMs = gameDate - now;
        const diffHours = diffMs / (1000 * 60 * 60);
        
        // Game started more than 4 hours ago = ended
        if (diffHours < -4) {
            return { status: 'ended', label: 'FINAL', color: '#6B7280', icon: 'checkmark-circle-outline' };
        }
        // Game is live or recently started (within last 4 hours)
        else if (diffHours < 0) {
            return { status: 'live', label: 'LIVE', color: '#22C55E', icon: 'radio-button-on', pulse: true };  // ⭐ GREEN for LIVE
        }
        // Game starts within next hour = starting soon
        else if (diffHours < 1) {
            return { status: 'soon', label: 'SOON', color: '#F59E0B', icon: 'alarm-outline' };
        }
        // Upcoming
        else {
            return { status: 'upcoming', label: 'UPCOMING', color: '#3B82F6', icon: 'time-outline' };
        }
    }, [gameDate, referenceTime]);
    
    // Format date/time for display
    const formattedDateTime = useMemo(() => {
        if (!gameDate) return null;
        try {
            const now = new Date();
            const isToday = gameDate.toDateString() === now.toDateString();
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const isTomorrow = gameDate.toDateString() === tomorrow.toDateString();
            const dateLabel = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            const timeStr = gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
            return { dateLabel, timeStr, fullDate: gameDate };
        } catch {
            return null;
        }
    }, [gameDate]);
    
    // Headshot/photo detection
    const headshotUrl = workingOption?._player_headshot || workingOption?._headshot_url || 
                        raw._player_headshot || raw._headshot_url || null;
    
    // 🏥 Injury detection
    const injuryStatus = raw._injury_status || workingOption?._injury_status || null;
    const isInjured = raw._is_injured || workingOption?._is_injured || false;
    const injuryDetails = raw._injury_details || workingOption?._injury_details || null;
    
    // Main/alt detection
    // ⭐ Use our computed true main line (based on book count, not backend flag)
    const isMainLine = isTrueMainLine(workingOption);
    const isAltLine = !isMainLine;
    
    // Status detection - use computed gameStatus OR explicit flags
    const isNoOddsYet = workingOption?._noOddsYet === true;
    const isLive = workingOption?._isLive === true || gameStatus.status === 'live';
    const isEnded = workingOption?._isEnded === true || gameStatus.status === 'ended';
    const isSoon = gameStatus.status === 'soon';
    const isUpcoming = gameStatus.status === 'upcoming';
    
    // Player name extraction for grouped mode
    const playerName = useMemo(() => {
        if (!isPlayerProp) return null;
        const sel = selection.replace(/\s*\b(over|under)\s*[\d.]+\s*$/i, '').replace(/\s*\b(over|under)\s*$/i, '').trim();
        return sel || null;
    }, [selection, isPlayerProp]);
    
    // Get line display - shows full "Over 225.5" or "Under 10.5" or "+4.5" for spreads
    const getLineDisplay = (opt) => {
        const sel = sanitizeSelectionString(opt?.raw?.selection || opt?.selection || '');
        const rawLineValue = opt?.lineValue || opt?.raw?.selection_line || opt?.raw?.handicap || opt?.raw?.line || '';
        const marketType = (opt?.raw?.market || opt?.title || '').toLowerCase();
        const isOver = /\bover\b/i.test(sel);
        const isUnder = /\bunder\b/i.test(sel);
        
        // ⭐ FIX: Ensure lineValue is numeric, not "over"/"under" string
        const isNumeric = (val) => val && !isNaN(parseFloat(val)) && !/^(over|under)$/i.test(String(val));
        let lineValue = isNumeric(rawLineValue) ? rawLineValue : '';
        
        // Extract numeric line value from selection if not provided
        let extractedLine = lineValue;
        if (!extractedLine) {
            const match = sel.match(/[+-]?\d+\.?\d*/);
            if (match) extractedLine = match[0];
        }
        
        // For Over/Under markets - only show direction + number, avoid "Over over" or "Under under"
        if (isOver && extractedLine && isNumeric(extractedLine)) return `Over ${extractedLine}`;
        if (isUnder && extractedLine && isNumeric(extractedLine)) return `Under ${extractedLine}`;
        
        // For spreads, show the line with +/- sign.
        // Strip any pre-existing leading '+' from extractedLine before re-prefixing to
        // prevent "++0.5" when the API already returns the sign in the value string.
        if (extractedLine && !isNaN(parseFloat(extractedLine))) {
            const num = parseFloat(extractedLine);
            const strippedLine = String(extractedLine).replace(/^\+/, '');
            return num > 0 ? `+${strippedLine}` : `${strippedLine}`;
        }
        
        // For moneyline or other markets without lines, clean up the selection
        // Remove "main" or "alt" suffixes AND avoid returning just "over"/"under"
        let cleanSel = sel
            .replace(/\s*main\s*$/i, '')
            .replace(/\s*alt\s*$/i, '')
            .trim();
        
        // ⭐ FIX: Don't return just "Over" or "Under" without a line number
        if (/^(over|under)$/i.test(cleanSel)) {
            // Try to find the team name from raw data instead
            const teamName = opt?.raw?.team || opt?.raw?.home_team || opt?.raw?.away_team || '';
            if (teamName) return teamName;
            return marketType.includes('total') ? 'Total' : cleanSel;
        }
        
        // If it's moneyline, just show the team/selection name
        if (marketType.includes('moneyline') || marketType.includes('money line')) {
            return cleanSel || 'Moneyline';
        }
        
        return cleanSel || sel || '—';
    };
    
    // Get clean title for display (market type without the line value)
    const getCleanTitle = () => {
        const marketName = raw.market || workingOption?.title || '';
        // Remove any line values, direction, and main/alt suffixes from the title
        let cleanTitle = marketName
            .replace(/\s*[+-]?\d+\.?\d*\s*$/g, '')  // Remove trailing numbers
            .replace(/\s*•\s*(over|under|main|alt)\s*$/i, '')  // Remove "• over/under/main/alt"
            .replace(/\s*(over|under|main|alt)\s*$/i, '')  // Remove trailing direction/type
            .trim();
        
        // ⭐ FIX: If title is ONLY "Over" or "Under" (direction), don't show it 
        // The direction will be shown in getLineDisplay() instead
        if (/^(over|under)$/i.test(cleanTitle)) {
            // Try to get a better title from selection (player name + market type)
            const sel = raw.selection || workingOption?.description || selection || '';
            // Extract player/team name if possible
            const nameMatch = sel.replace(/\s*\b(over|under)\s*[\d.]*\s*$/i, '').trim();
            if (nameMatch && nameMatch.length > 0 && !/^(over|under)$/i.test(nameMatch)) {
                return nameMatch;
            }
            return ''; // Return empty to avoid duplicate "Under under"
        }
        
        return cleanTitle || marketName;
    };
    
    // Get clean description for display (team/player without duplicate line values)
    const getCleanDescription = () => {
        const sel = sanitizeSelectionString(raw.selection || workingOption?.description || selection || '');
        const lineValue = workingOption?.lineValue || raw.selection_line || raw.handicap || '';
        // Remove duplicate line values, direction info, and clean up. An empty
        // lineValue (moneylines) must skip the duplicate step: a regex built from
        // '' matches between every character and spaced the text out ("N e w  Y o r k").
        const lineText = String(lineValue ?? '').trim();
        const escapedLine = lineText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        let clean = sel
            .replace(/\s*main\s*$/i, '')  // Remove trailing "main"
            .replace(/\s*alt\s*$/i, '')  // Remove trailing "alt"  
            .replace(lineText ? new RegExp(`\\s*${escapedLine}\\s*${escapedLine}`, 'g') : /(?!)/, ` ${lineText}`)  // Remove duplicates
            .replace(/\s*\b(over|under)\s*[+-]?\d+\.?\d*\s*$/i, '')  // Remove "over/under X.X" from end (\b: not the "under" in "Thunder")
            .replace(/\s*[+-]?\d+\.?\d*\s*[+-]?\d+\.?\d*\s*$/g, '')  // Remove double numbers at end
            .replace(/\s*[+-]?\d+\.?\d*\s*$/g, '')  // Remove single trailing number
            .trim();
        // A selection that is only a side and line ("Over 6") names no team: say
        // whose line it is, so a game total isn't mistaken for one team's total.
        if (!clean || /^(over|under)$/i.test(clean)) {
            if (raw.player_name) return raw.player_name;   // player prop whose selection is only "Over 24.5"
            const game = raw.away_team && raw.home_team ? `${raw.away_team} @ ${raw.home_team}` : '';
            const market = String(raw.market || workingOption?.title || '').toLowerCase();
            if (game && /\btotal\b/.test(market) && !/\bteam total\b/.test(market)) return `Game total · ${game}`;
            if (game) return game;
        }
        return clean || sel;
    };
    
    // Handle selection
    const handleSelect = () => {
        if (onSelect) {
            onSelect(workingOption);
        }
    };
    
    // Returns true when a deep link URL plausibly belongs to the stated book.
    // Derives a 4+ char token from the first word of the display name and checks
    // whether it appears in the URL's hostname. Catches cross-brand contamination
    // (e.g. a Betfair Exchange link stored with a Caesars URL from a stale merge).
    const _isDeepLinkPlausible = (bookDisplayName, url) => {
        if (!url || !bookDisplayName) return true;
        try {
            const domain = new URL(url).hostname.toLowerCase();
            // Strip parenthetical suffixes ("(Lay)", "(Australia)"), take first word.
            const token = bookDisplayName
                .replace(/\s*\(.*\)/, '')
                .trim()
                .split(/\s+/)[0]
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '');
            if (token.length < 4) return true; // too short to validate reliably
            return domain.includes(token);
        } catch {
            return true; // malformed URL → allow, will just fail to open
        }
    };

    // Handle deep link
    const handleOpenDeepLink = (book) => {
        const bookData = bookOddsData.find(b => b.book === book);
        let deepLink = bookData?.odds?.deep_link || bookData?.odds?.deeplink;
        
        // Handle object format deep links (desktop/ios/android)
        if (deepLink && typeof deepLink === 'object') {
            deepLink = deepLink.desktop || deepLink.web || deepLink.ios || deepLink.android;
        }

        // Reject links whose domain doesn't match the book name — this catches stale
        // deep_link values written by cross-fixture merges (e.g. Betfair URL = Caesars URL).
        if (deepLink && typeof deepLink === 'string' && !_isDeepLinkPlausible(book, deepLink)) {
            console.warn(`[DeepLink] Rejected suspicious link for "${book}": domain mismatch → ${deepLink}`);
            return; // treat as no link
        }
        
        if (deepLink && onOpenDeepLink) {
            onOpenDeepLink(deepLink, book);
        } else if (deepLink && typeof deepLink === 'string' && deepLink.startsWith('http')) {
            // Fallback: Open directly if onOpenDeepLink not provided
            if (Platform.OS === 'web') {
                window.open(deepLink, '_blank', 'noopener,noreferrer');
            }
        }
    };
    
    // ═══════════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════════════════════════════════
    

  return { _isDeepLinkPlausible, activeBookFilter, activeSide, cardSides, setActiveSide, altLinesScrollRef, awayLogoUrl, bookCount, bookOddsData, canShowDevCopy, canShowMath, consensusFairData, consensusIncludedSet, consensusPolicy, copyFlash, defaultSelectedBook, devigMethods, effectiveFairProb, ev, excludedBookSet, fairPrice, filteredBookCount, formattedDateTime, gameStatus, getCleanDescription, getCleanTitle, getLineDisplay, groupedLines, handleCopyBooks, handleOpenDeepLink, handleSelect, hasOriginalAltLines, headshotUrl, highlightPreferredBook, homeLogoUrl, implied, injuryDetails, injuryStatus, isAdmin, isAltLine, isEnded, isInjured, isLive, isMainLine, isMobileLayout, isPlayerProp, isSGP, isSelected, isTrueMainLine, kellyUnits, lineSortMode, marketAverage, minBooksFilter, mountAnim, myBooks, payout, perBookEV, playerName, preserveLineOrder, price, raw, requestedDirection, requestedLine, selectedBook, selectedLineIndex, setLineSortMode, setMinBooksFilter, setSelectedBook, setSelectedLineIndex, setShowAltLines, setShowMainOnly, setShowMath, setShowNerdMath, setSortBooksBy, setStake, sgpLegs, showAltLines, showMainAltBadge, showMainOnly, showMath, showNerdMath, sortBooksBy, sortedDisplayedBooks, sortedLines, stake, suggestedWager, userHasSelectedRef, userHasSelectedSortRef, workingOption };
}
